import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { InputContainer, InputLabel, InputError } from './InputBase';

export interface TextAreaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'style'
> {
  // Core props
  id?: string;
  label?: string;
  error?: string;

  // Configuration
  variant?: 'default' | 'plain';
  density?: 'normal' | 'compact' | 'none';
  autoGrow?: boolean;
  maxRows?: number;
  minHeight?: number | string;
  maxHeight?: number | string;
  containerClassName?: string;
  /** Action aligned with the control, excluding its label, error, and outer spacing. */
  trailingAction?: React.ReactNode;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      label,
      value,
      onChange,
      rows = 1,
      required = false,
      error,
      className = '',
      placeholder,
      disabled,
      autoGrow = false,
      variant = 'default',
      density = 'normal',
      maxRows,
      minHeight,
      maxHeight,
      onKeyDown,
      containerClassName,
      trailingAction,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    // Expose internal ref to parent
    useImperativeHandle(ref, () => internalRef.current as HTMLTextAreaElement);

    const isInsideHiddenContainer = useCallback((el: HTMLTextAreaElement) => {
      return Boolean(el.closest('[hidden]'));
    }, []);

    const calculateHeight = useCallback(() => {
      const el = internalRef.current;
      if (!el) return;

      // Auto-grow measurements while hidden can collapse fields (for example to ~14px).
      if (isInsideHiddenContainer(el)) return;

      // Reset height to auto to get correct scrollHeight
      el.style.height = 'auto';

      // Default manual constraints
      if (minHeight)
        el.style.minHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
      if (maxHeight)
        el.style.maxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : String(maxHeight);

      const cs = window.getComputedStyle(el);
      const parsedLineHeight = parseFloat(cs.lineHeight);
      const parsedFontSize = parseFloat(cs.fontSize);
      const lineHeight = Number.isFinite(parsedLineHeight)
        ? parsedLineHeight
        : Number.isFinite(parsedFontSize)
          ? parsedFontSize * 1.2
          : 16;
      const paddingTop = parseFloat(cs.paddingTop) || 0;
      const paddingBottom = parseFloat(cs.paddingBottom) || 0;
      const borderTop = parseFloat(cs.borderTopWidth) || 0;
      const borderBottom = parseFloat(cs.borderBottomWidth) || 0;
      const minRows = Math.max(1, rows);
      const minRowsHeight =
        lineHeight * minRows + paddingTop + paddingBottom + borderTop + borderBottom;

      // Enforce baseline from rows + computed typography so bad measurements can't collapse the field.
      let targetHeight = Math.max(el.scrollHeight, minRowsHeight);

      let shouldEnableVerticalScroll = false;

      // If maxRows is set, calculate max height based on line height
      if (maxRows) {
        const maxPx = lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom;

        if (targetHeight > maxPx) {
          targetHeight = maxPx;
          shouldEnableVerticalScroll = true;
        } else {
          shouldEnableVerticalScroll = false;
        }
      }

      if (className.includes('h-full') || className.includes('min-h-full')) {
        el.style.minHeight = `${targetHeight}px`;
        el.style.height = '100%';
      } else {
        el.style.height = `${targetHeight}px`;
      }

      if (maxRows) {
        el.style.overflowY = shouldEnableVerticalScroll ? 'auto' : 'hidden';
        return;
      }

      // With maxHeight-only auto-grow, switch to internal scrolling once clamped.
      if (maxHeight) {
        el.style.overflowY = el.scrollHeight > el.clientHeight + 1 ? 'auto' : 'hidden';
      } else {
        el.style.overflowY = 'hidden';
      }
    }, [maxRows, minHeight, maxHeight, className, isInsideHiddenContainer, rows]);

    // Adjust on value change
    useLayoutEffect(() => {
      if (autoGrow) calculateHeight();
    }, [value, autoGrow, calculateHeight]);

    // Adjust on resize to maintain auto-grow after layout changes.
    useEffect(() => {
      if (!autoGrow || !internalRef.current || typeof ResizeObserver === 'undefined') return;

      const ro = new ResizeObserver(() => {
        calculateHeight();
      });

      ro.observe(internalRef.current);
      return () => ro.disconnect();
    }, [autoGrow, calculateHeight]);

    // Re-run auto-grow when parent sections toggle hidden/visible.
    useEffect(() => {
      if (!autoGrow || !internalRef.current || typeof MutationObserver === 'undefined') return;
      const el = internalRef.current;
      const visibilityContainer = el.closest('[data-portal-view-tab]') ?? el.closest('section');
      if (!visibilityContainer) return;

      const observer = new MutationObserver(() => {
        if (!el.closest('[hidden]')) {
          calculateHeight();
        }
      });

      observer.observe(visibilityContainer, {
        attributes: true,
        attributeFilter: ['hidden'],
      });

      return () => observer.disconnect();
    }, [autoGrow, calculateHeight]);

    // Styles
    const baseStyles =
      'w-full text-glyph-primary placeholder:text-glyph-disabled/60 font-body transition-all duration-fast ease-smooth disabled:cursor-not-allowed disabled:opacity-60';

    const variantStyles =
      variant === 'default'
        ? 'bg-input border border-outline-default/60 rounded-xl focus-standard'
        : 'bg-transparent border-transparent resize-none focus:bg-input focus:border-outline-default/60 rounded-md focus-subtle'; // Plain for grid

    const densityStyles =
      density === 'none'
        ? ''
        : [
            variant === 'plain' && density === 'compact' ? 'px-2' : 'px-3',
            variant === 'default' && density === 'normal' ? 'py-2' : 'pt-1 pb-2',
            'text-sm',
            density === 'normal' ? 'leading-normal' : 'leading-snug',
          ].join(' ');

    const validationStyles = error ? 'border-glyph-critical focus:border-glyph-critical' : '';

    const combinedClasses = [
      baseStyles,
      variantStyles,
      densityStyles,
      validationStyles,
      trailingAction ? 'min-w-0 flex-1' : '',
      'resize-none',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const errorId = error && id ? `${id}-error` : undefined;
    const describedBy = [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined;

    const textAreaElement = (
      <textarea
        ref={internalRef}
        id={id}
        value={value}
        onChange={onChange}
        onInput={autoGrow ? calculateHeight : undefined}
        onKeyDown={onKeyDown}
        rows={rows}
        className={combinedClasses}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        {...props}
      />
    );

    const control = trailingAction ? (
      <div className="flex min-w-0 items-center gap-1">
        {textAreaElement}
        {trailingAction}
      </div>
    ) : (
      textAreaElement
    );

    if (variant === 'plain') {
      // For grid view, we just render the textarea (maybe with error tooltip later, but for now simple)
      return (
        <div className="relative w-full">
          {control}
          {/* Simple error indicator for plain mode if needed, or rely solely on border color */}
        </div>
      );
    }

    // Default behavior wraps in container
    return (
      <InputContainer className={containerClassName}>
        <InputLabel label={label} required={required} htmlFor={id} />
        {control}
        <InputError id={errorId} error={error} />
      </InputContainer>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
